import { useSetAtom } from 'jotai';
import { clearSelectionsAtom, erroredOperationAtom } from '~/atoms/no-code-atoms';
import { api } from '~/trpc-extra/api';
import type { TestContentMutationContext } from '../callback-handles';
import { cancelTestContentQueries, handleMutationError, handleMutationSuccess } from '../callback-handles';
import { useLiveStateManager } from '../../useLiveStateManager';
import { OPTIMISTIC_OPERATION_KEY } from '../optimistic-handles';

export const useCombineSteps = ({ testContentId }: { testContentId: string }) => {
  const trpcUtils = api.useUtils();

  const clearSelections = useSetAtom(clearSelectionsAtom);
  const setErroredOperation = useSetAtom(erroredOperationAtom);

  const { recorderState, updateLiveState } = useLiveStateManager();

  const combineStepsMutation = api.testStep.combineSteps.useMutation<TestContentMutationContext>({
    onSuccess: async () => {
      await handleMutationSuccess({
        message: 'Combined the steps',
        trpcUtils,
        testContentId,
      });
      setErroredOperation(undefined);
      clearSelections();
    },
    onError: (_error, _variables, context) =>
      handleMutationError({
        message: 'Failed to combine the steps',
        trpcUtils,
        testContentId,
        context,
      }),
    onMutate: async ({ stepIds }) => {
      await cancelTestContentQueries({ trpcUtils, testContentId });
      const prevData = trpcUtils.testContent.get.getData({ id: testContentId });
      if (!prevData) {
        return { prevData: null };
      }

      if (stepIds.length < 2) {
        return { prevData };
      }

      const firstStep = prevData.steps.find((step) => step.id === stepIds[0]);
      if (!firstStep) {
        return { prevData };
      }

      const otherStepIds = new Set(stepIds.slice(1));

      if (recorderState?.stepId && otherStepIds.has(recorderState.stepId)) {
        updateLiveState((prev) => ({
          ...prev,
          recorderCursor: {
            stepId: firstStep.id,
            stepOrder: firstStep.stepOrder,
            operation: firstStep.operations.at(-1),
          },
        }));
      }

      const otherStepsOps = prevData.steps
        .filter((step) => otherStepIds.has(step.id))
        .flatMap((step) => step.operations || []);

      firstStep.operations = [
        ...(firstStep.operations || []),
        ...otherStepsOps.map((op) => ({
          ...op,
          id: `${OPTIMISTIC_OPERATION_KEY}-${op.id}-${Date.now()}`,
          order: (firstStep.operations || []).length + op.order,
        })),
      ];

      const newData = {
        ...prevData,
        steps: prevData.steps.filter((step) => !otherStepIds.has(step.id)),
      };

      trpcUtils.testContent.get.setData({ id: testContentId }, newData);
      return { prevData };
    },
  });

  return combineStepsMutation;
};
